/**
 * @module web/api
 * @description This module contains functions used to interact with the backend api
 */

import { showToast } from 'components/toast'

// Use api.localhost in local; switch between prod/staging API domains in hosted envs
const hostname = window.location.hostname
const endpoint = hostname === 'localhost'
	? 'http://api.localhost'
	: (hostname.includes('staging.')
		? 'https://api.staging.allforonelearn.com'
		: 'https://api.allforonelearn.com')

// const endpoint = 'https://4o20rcnrdi.execute-api.us-east-1.amazonaws.com'

/**
 * A helper function to send a get request to the backend api
 *
 * @param path {string} - The request path
 * @param params {object} - The request parameters as a json object
 * @param headers {object} - The request headers json object
 * @returns {Promise<object>} A promise that resolves to a json object
 */
const get = async (path, params = {}, headers = {}) => {
	const sanitizedParams = Object.fromEntries(
		Object.entries(params).filter(([_, val]) => val !== undefined)
	)

	const response = await fetch(
		`${endpoint}/${path}?${new URLSearchParams(sanitizedParams)}`,
		{
			method: 'GET',
			credentials: 'include',
			headers,
		},
	)

	// err-14: was a blocking alert() that then parsed the body anyway,
	// rejecting unhandled on non-JSON error bodies. Fail properly instead.
	if (!response.ok) {
		showToast('Something went wrong. Please try again.')
		throw new Error(`GET /${path} failed: ${response.status}`)
	}

	return response.json()
}

/**
 * A helper function to send a post request to the backend api
 *
 * @param path {string} - The request path
 * @param body {object} - The request body json object
 * @param headers {object} - The request headers json object
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const post = async (path, body = {}, headers = {}) =>{

	const response = await fetch(`${endpoint}/${path}`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'content-type': 'application/json',
			...headers,
		},
		body: JSON.stringify(body),
	})

	return response
}

/**
 * Sends a backend request to create a new user
 *
 * @param firstName {string} - The user's first name
 * @param lastName {string} - The user's last name
 * @param email {string} - The user's email
 * @param password {string} - The user's password
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const signup = async (firstName, lastName, email, password) =>
	post(
		'signup',
		{ firstName, lastName, email, password },
	)

/**
 * Sends a backend request to verify a user
 *
 * @param username {string} - The user's username
 * @param confirmationCode {string} - The user's confirmation code
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const verify = async (username, confirmationCode) =>
	post(
		'verify',
		{ username, confirmationCode },
	)

/**
 * Sends a backend request to create new confirmation code
 *
 * @param username {string} - The user's username
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const resendCode = async username =>
	post(
		'verify/resend-code',
		{ username },
	)

/**
 * Sends a backend request to login the user
 *
 * @param username {string} - The user's username
 * @param password {string} - The user's password
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const login = async (username, password) =>
	post(
		'login',
		{ username, password },
	)

/**
 * Sends a backend request to respond to an auth challenge
 *
 * @param body {object} - Payload including challenge, session, username and challenge-specific fields
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const respondToAuthChallenge = async body =>
	post(
		'login/respond-to-challenge',
		body,
	)

/**
 * Sends a backend request to logout the user
 *
 * @param username {string} - The user's username
 * @param password {string} - The user's password
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const logout = async () =>
	post(
		'logout',
		{},
	)

/**
 * Sends a backend request to initiate the forgot-password procedure
 *
 * @param username {string} - The user's username
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const forgotPassword = async username =>
	post(
		'forgot-password',
		{ username },
	)

/**
 * Sends a backend request to reset the user's password
 *
 * @param confirmationCode {string} - The user's confirmation code
 * @param username {string} - The user's username
 * @param password {string} - The user's password
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const resetPassword = async (confirmationCode, username, password) =>
	post(
		'reset-password',
		{ confirmationCode, username, password },
	)

/**
 * Sends a backend request to retrieve user details
 *
 * @returns {Promise<object>} A promise that resolves to a json object
 */
const getUserDetails = async () =>
	get(
		'users/me',
		{},
	)

/**
 * Sends a backend request to retrieve user details
 *
 * @param details {object} - A json object contianing the details to update
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const updateUserDetails = async details =>
{	// Filter out any keys that aren't allowed to be updated
	const allowedKeys = [
		'phoneNumber',
		'title',
		'schoolOrOrg',
		'gradesTaught',
		'subjectsTaught',
		'profilePicture'
	]
	details = Object.fromEntries(
		Object.entries(details).filter(([key]) => allowedKeys.includes(key))
	)

	return post(
		'users/me',
		details,
	)
}

/**
 * Sends a backend request to upload files
 *
 * @param files {File[]|HTMLInputElement} - Array of File objects or a file input element
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const uploadFiles = async files => {
	const formData = new FormData()

	// Handle both File[] and HTMLInputElement for backward compatibility
	const fileArray = Array.isArray(files) ? files : Array.from(files.files || [])

	for (const file of fileArray) {
		formData.append('files[]', file)
	}

	return fetch(`${endpoint}/files`, {
		method: 'POST',
		credentials: 'include',
		body: formData,
	})
}

/**
 * Sends a backend request to upload files (easier version)
 *
 * @param files {File[]} - Array of File objects to upload
 * @returns {Promise<Response>} A promise that resolves to an HTTP response object
 */
const uploadFilesDirect = async files => {
	const formData = new FormData()

	for (const file of files) {
		formData.append('files[]', file)
	}

	return fetch(`${endpoint}/files`, {
		method: 'POST',
		credentials: 'include',
		body: formData,
	})
}

/**
 * Sends a backend request to retrieve lessons that belong to the logged-in user
 *
 * @returns {Promise<object>} A promise that resolves to a json object
 */
const getUserLessons = async pageToken =>
	get(
		'lessons/user-lessons',
		{ pageToken },
	)

export {
	signup,
	verify,
	resendCode,
	login,
	logout,
	forgotPassword,
	resetPassword,
	getUserDetails,
	updateUserDetails,
	uploadFiles,
	uploadFilesDirect,
	getUserLessons,
	respondToAuthChallenge,
}